// src/components/weather/TmyImportManager.jsx
// Import & manage user-supplied TMY weather datasets for a project.
//
// Accepted upload formats: platform CSV template, PVGIS TMY CSV, EnergyPlus EPW,
// SolarGIS (CSV/XLSX) and Meteonorm (CSV).
//
// Several sources can also be fetched server-side, listed by GET /tmy/providers
// rather than hardcoded here so a provider added on the backend needs no
// frontend release. Three shapes of source exist, and the form adapts per entry:
//   - credential-free (PVGIS)
//   - platform-credentialled, where the operator holds the account (CAMS)
//   - user-credentialled, where the user supplies their own paid subscription
//     key (Solcast, Solargis, Meteonorm, SolarAnywhere)
// A user-supplied key is transient: it lives in component state for the single
// request and is cleared on dialog close, source change and fetch completion.
// This dialog manages the dataset files (import, inspect, download, delete).
// Assigning a dataset to PV or Wind is done in WeatherSourceSelector, which is
// the single authoritative control for that choice.
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLanguage } from "../../context/LanguageContext";
import { weatherReportText, weatherReportCount } from "./weatherReportText";
import {
  deleteProjectTmy,
  downloadProjectTmy,
  downloadTmyTemplate,
  fetchProjectTmyFromProvider,
  importProjectTmy,
  listProjectTmy,
  listTmyProviders,
} from "../../services/api";

// Covers both routes a dataset can arrive by: an upload parser or a server-side
// fetch. 'solargis' and 'meteonorm' are reachable either way; 'cams', 'solcast'
// and 'solaranywhere' are fetch-only. A key missing here renders a blank format
// column, so this must track weather_providers.PROVIDERS on the backend.
const FORMAT_LABELS = {
  csv_template: "CSV template",
  pvgis: "PVGIS",
  pvgis_year: "PVGIS (historical year)",
  epw: "EPW",
  solargis: "SolarGIS",
  meteonorm: "Meteonorm",
  cams: "CAMS",
  solcast: "Solcast",
  solaranywhere: "SolarAnywhere",
};

export default function TmyImportManager({
  projectId,
  accessToken,
  buttonProps = {},
  hasPv = true,
  hasWind = true,
  onChanged,
}) {
  const { isGreek, translateText } = useLanguage();
  const reportText = (message) => weatherReportText(message, isGreek, translateText);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imports, setImports] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [fetching, setFetching] = useState(false);
  // Server-side fetch controls. `providers` comes from the backend registry so a
  // new source needs no frontend release.
  const [providers, setProviders] = useState([]);
  const [providerKey, setProviderKey] = useState("pvgis");
  const [fetchYear, setFetchYear] = useState("");
  // The user's own commercial subscription key. Transient by design: held here
  // only, never written to storage, and cleared whenever the dialog closes, the
  // source changes, or a fetch finishes. See the reset effects below.
  const [apiKey, setApiKey] = useState("");
  // Per-dataset "warnings expanded" state, keyed by import id. Collapsed by
  // default so the table stays scannable; warnings are advisory (the import
  // succeeded) so they must never crowd out the row itself.
  const [expandedWarnings, setExpandedWarnings] = useState({});
  const fileInputRef = useRef(null);

  const toggleWarnings = (id) =>
    setExpandedWarnings((prev) => ({ ...prev, [id]: !prev[id] }));

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await listProjectTmy(projectId, accessToken);
      setImports(data?.imports || []);
      setError("");
    } catch (err) {
      // Benign when the project isn't saved yet (the backend has no record of
      // it) — show "no datasets" rather than a scary error.
      setImports([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, accessToken]);

  useEffect(() => {
    if (open) {
      // Fresh session each time the dialog opens: stale import results from a
      // previous upload would otherwise linger until a page refresh.
      setReport(null);
      setError("");
      setSelectedFile(null);
      setDragActive(false);
      setFetching(false);
      // A credential must not survive a dialog close/reopen.
      setApiKey("");
      refresh();
    }
  }, [open, refresh]);

  // Load the source registry once the dialog opens. A failure here is not fatal:
  // fall back to PVGIS alone, which needs no credentials and always exists, so
  // the dialog stays usable if the endpoint is unavailable.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await listTmyProviders(accessToken);
        if (cancelled) return;
        const list = data?.providers || [];
        setProviders(list);
        const fallbackYear = data?.latest_complete_year;
        if (fallbackYear) setFetchYear(String(fallbackYear));
        if (!list.some((p) => p.key === providerKey)) {
          setProviderKey(data?.default || "pvgis");
        }
      } catch {
        if (!cancelled) {
          setProviders([
            {
              key: "pvgis",
              label: "PVGIS (TMY)",
              requires_api_key: false,
              needs_year: false,
              available: true,
            },
          ]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // providerKey is deliberately excluded: this should run on open, not on every
    // source change, or switching source would refetch the catalogue each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accessToken]);

  const activeProvider =
    providers.find((p) => p.key === providerKey) || null;
  const firstYear = activeProvider?.year_range?.first;
  const lastYear = activeProvider?.year_range?.last;
  useEffect(() => {
    if (!activeProvider?.needs_year || lastYear == null) return;
    setFetchYear((current) => !current || Number(current) > lastYear || (firstYear != null && Number(current) < firstYear)
      ? String(lastYear) : current);
  }, [providerKey, activeProvider?.needs_year, firstYear, lastYear]);
  const invalidYear = activeProvider?.needs_year && (!String(fetchYear).trim() || !Number.isInteger(Number(fetchYear))
    || (firstYear != null && Number(fetchYear) < firstYear) || (lastYear != null && Number(fetchYear) > lastYear));

  // Switching source drops any key typed for the previous one — a Solcast key
  // must never be sent to Solargis.
  useEffect(() => {
    setApiKey("");
  }, [providerKey]);

  const stageFile = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setReport(null);
    setError("");
  };

  const handleFileChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    stageFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (uploading) return;
    stageFile(event.dataTransfer?.files?.[0]);
  };

  const handleImport = async () => {
    if (!selectedFile || !projectId) return;
    setUploading(true);
    setReport(null);
    setError("");
    try {
      const result = await importProjectTmy(projectId, selectedFile, accessToken);
      setReport(result?.report || null);
      setSelectedFile(null);
      await refresh();
      onChanged?.();
    } catch (err) {
      if (err?.report) {
        setReport(err.report); // validation errors — show the report
      } else {
        setError(err?.message || "Import failed.");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFetch = async () => {
    if (!projectId || !providerKey) return;
    setFetching(true);
    setReport(null);
    setError("");
    try {
      const result = await fetchProjectTmyFromProvider(
        projectId,
        providerKey,
        accessToken,
        {
          year: activeProvider?.needs_year ? fetchYear : null,
          apiKey: activeProvider?.requires_api_key ? apiKey : null,
        },
      );
      setReport(result?.report || null);
      // Drop the credential as soon as it has served its purpose, so it is not
      // sitting in component state while the user reads the report.
      setApiKey("");
      await refresh();
      onChanged?.();
    } catch (err) {
      if (err?.report) {
        setReport(err.report);
      } else {
        setError(err?.message || "Fetch failed.");
      }
    } finally {
      setFetching(false);
    }
  };

  const fetchBlocked =
    fetching ||
    uploading ||
    !projectId ||
    !activeProvider ||
    activeProvider.available === false ||
    (activeProvider.requires_api_key && !apiKey.trim()) ||
    invalidYear;

  // Keyed "<rowId>:<variant>" so the spinner replaces only the button that was
  // clicked. A download is a network round trip plus a server-side storage fetch;
  // with no feedback at all it reads as a dead button and invites double clicks.
  const [downloading, setDownloading] = useState("");

  const handleDownload = async (row, variant) => {
    const key = `${row.id}:${variant}`;
    if (downloading) return;                 // one at a time, avoids double-fire
    setError("");
    setDownloading(key);
    const stem = (row.original_filename || "TMY").replace(/\.[^.]+$/, "");
    const fallback =
      variant === "raw" ? row.original_filename || `${stem}.csv` : `${stem}_normalized.csv`;
    try {
      await downloadProjectTmy(projectId, row.id, accessToken, fallback, variant);
    } catch (err) {
      setError(err?.message || "Failed to download dataset.");
    } finally {
      setDownloading("");
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteProjectTmy(projectId, row.id, accessToken);
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err?.message || "Failed to delete dataset.");
    }
  };

  const anyActive = imports.some(
    (r) => (hasPv && r.is_active_pv) || (hasWind && r.is_active_wind)
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFileIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        {...buttonProps}
      >
        Weather data{anyActive ? " •" : ""}
      </Button>

      {/* lg rather than md: the table grew a Source column and split the download
          action into Normalized/Raw, which squeezed the File column enough to
          truncate every synthetic fetch filename. */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        {/* Titled for what this dialog actually manages. It handles BOTH routes -
            files the user uploads and datasets the platform fetches - so calling
            it "Imported TMY" contradicted the Weather source panel, which now
            lists those as two separate sources. */}
        <DialogTitle>Weather data files</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Manage the weather datasets available to this project. Two ways in:{" "}
              <b>upload your own file</b> (platform CSV template, PVGIS TMY CSV,
              EnergyPlus EPW, SolarGIS CSV/XLSX, Meteonorm CSV), or{" "}
              <b>fetch from a provider</b> for the project coordinates. Uploads appear
              under <b>Imported TMY</b> in the Weather source panel and fetched
              datasets under <b>External provider</b>; that panel is where you choose
              which one drives PV or Wind.{" "}
              <Link
                component="button"
                type="button"
                onClick={() => downloadTmyTemplate(accessToken)}
                sx={{ fontWeight: 700 }}
              >
                Download CSV template
              </Link>
            </Typography>

            {!projectId && (
              <Alert severity="info">
                Weather datasets are stored per project. Save or open a project
                first (File → Save / Open), then upload or fetch your weather data here.
              </Alert>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.epw,.xlsx,.xls,.dat,.tsv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChosen}
            />
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploading && projectId) setDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              onClick={() => {
                if (!uploading && projectId) fileInputRef.current?.click();
              }}
              sx={{
                border: "2px dashed",
                borderColor: dragActive ? "primary.main" : "divider",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: projectId && !uploading ? "pointer" : "default",
                bgcolor: dragActive ? "action.hover" : "transparent",
                transition: "background-color 120ms, border-color 120ms",
                opacity: projectId ? 1 : 0.6,
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Drag &amp; drop a file here, or <b>browse</b>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                CSV template, PVGIS TMY, EPW, SolarGIS or Meteonorm
              </Typography>
            </Box>

            {/* Credential-free server-side retrieval: skips the manual
                download-then-upload round trip for PVGIS. */}
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap">
                <TextField
                  select
                  size="small"
                  label="Source"
                  value={providerKey}
                  onChange={(e) => setProviderKey(e.target.value)}
                  disabled={fetching || uploading}
                  sx={{ minWidth: 220 }}
                >
                  {providers.map((p) => (
                    <MenuItem key={p.key} value={p.key} disabled={p.available === false}>
                      {translateText(p.label)}
                      {p.available === false ? ` ${translateText("— not configured")}` : ""}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Dated sources (CAMS, Solargis) return one real calendar year
                    rather than a typical year, so the year is part of the request. */}
                {activeProvider?.needs_year && (
                  <TextField
                    size="small"
                    type="number"
                    label="Year"
                    value={fetchYear}
                    error={Boolean(invalidYear)}
                    helperText={firstYear != null && lastYear != null ? `${firstYear}–${lastYear}` : undefined}
                    onChange={(e) => setFetchYear(e.target.value)}
                    disabled={fetching || uploading}
                    inputProps={{
                      min: activeProvider?.year_range?.first ?? 2000,
                      max: activeProvider?.year_range?.last ?? new Date().getFullYear() - 1,
                    }}
                    sx={{ width: 120 }}
                  />
                )}

                {/* The user's own paid subscription key. type="password" and
                    autoComplete="off" so it is not shoulder-readable and not
                    offered up by the browser's saved-form autofill. Never stored. */}
                {activeProvider?.requires_api_key && (
                  <TextField
                    size="small"
                    type="password"
                    label="Your API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    disabled={fetching || uploading}
                    autoComplete="off"
                    inputProps={{ autoCorrect: "off", spellCheck: "false" }}
                    sx={{ minWidth: 240 }}
                  />
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    fetching ? <CircularProgress size={15} color="inherit" /> : <CloudDownloadOutlinedIcon />
                  }
                  disabled={fetchBlocked}
                  onClick={handleFetch}
                  sx={{ mt: 0.25 }}
                >
                  {fetching ? "Fetching…" : "Fetch for this site"}
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {activeProvider?.available === false
                  ? translateText(activeProvider.unavailable_reason ||
                    "This source is not configured on this deployment.")
                  : activeProvider?.requires_api_key
                    ? `${translateText(activeProvider.api_key_hint || "Requires your own subscription key.")} ${translateText("The key is used for this fetch only and is not stored.")}`
                    : translateText("No file or account needed — uses the project coordinates.")}
              </Typography>

              {activeProvider?.notes && (
                <Typography variant="caption" color="text.secondary">
                  {translateText(activeProvider.notes)}
                </Typography>
              )}

              {/* NOTE: the backend still reports `verified: false` for providers
                  whose fetch path has not been exercised against a live paid
                  subscription (Solcast, Solargis, Meteonorm, SolarAnywhere - see
                  weather_providers.PROVIDERS). That flag is deliberately NOT
                  surfaced here any more: it is operator-facing information, not
                  something to put in front of a customer mid-task. It remains in
                  the GET /tmy/providers response for our own use, and the fetch
                  itself still returns a warning in `report.warnings`. */}

              {fetching && (
                <Typography variant="caption" color="text.secondary">
                  Satellite sources can take up to a minute for a full year.
                  Leave this dialog open.
                </Typography>
              )}
            </Stack>

            {selectedFile && (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <UploadFileIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {selectedFile.name}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setSelectedFile(null)} disabled={uploading}>
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleImport}
                    disabled={uploading || !projectId}
                    startIcon={
                      uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />
                    }
                  >
                    {uploading ? "Importing…" : "Import"}
                  </Button>
                </Stack>
              </Stack>
            )}

            {error ? <Alert severity="error">{error}</Alert> : null}

            {report ? (
              <Stack spacing={1}>
                {(report.errors || []).length > 0 && (
                  <Alert severity="error">
                    <b>Import rejected:</b>
                    <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                      {report.errors.map((e, i) => (
                        <li key={i}>{reportText(e)}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {(report.warnings || []).length > 0 && (
                  <Alert severity="warning">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {report.warnings.map((w, i) => (
                        <li key={i}>{reportText(w)}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {/* Substitutions and conventions the run will apply. Info, not
                    warning: nothing is wrong with the file, but the user must
                    know what the simulation will actually use in place of the
                    variables it does not carry. */}
                {(report.info || []).length > 0 && (
                  <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />}>
                    <AlertTitle sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                      How this dataset will be used
                    </AlertTitle>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {report.info.map((m, i) => (
                        <li key={i}>{reportText(m)}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {report.stats && (report.errors || []).length === 0 && (
                  <Alert severity="success">
                    {/* Built as whole sentences per language rather than left
                        to the text-node translator: every clause here has a
                        number inside it, so there is no static node to match. */}
                    {isGreek
                      ? `Εισήχθησαν ${report.stats.rows} ωριαίες σειρές`
                      : `Imported ${report.stats.rows} hourly rows`}
                    {report.stats.annual_ghi_kwh_m2 != null &&
                      (isGreek
                        ? ` — ετήσια GHI ${report.stats.annual_ghi_kwh_m2} kWh/m²`
                        : ` — annual GHI ${report.stats.annual_ghi_kwh_m2} kWh/m²`)}
                    {report.stats.mean_wind_speed_ms != null &&
                      (isGreek
                        ? `, μέσος άνεμος ${report.stats.mean_wind_speed_ms} m/s`
                        : `, mean wind ${report.stats.mean_wind_speed_ms} m/s`)}
                    {report.stats.distance_from_project_km != null &&
                      (isGreek
                        ? `, ${report.stats.distance_from_project_km} km από τη θέση του έργου`
                        : `, ${report.stats.distance_from_project_km} km from project site`)}
                    .
                  </Alert>
                )}
              </Stack>
            ) : null}

            <Divider />

            {loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading datasets…
                </Typography>
              </Stack>
            ) : imports.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No weather datasets yet — upload a file or fetch one above.
                Generation runs use platform internal weather until you do.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    {/* Origin is not derivable from Format: source_format reads
                        "PVGIS TMY" whether the user uploaded the file or the
                        platform fetched it. The Weather source panel now treats
                        those as different sources, so the distinction has to be
                        visible here too. */}
                    <TableCell>Source</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell align="right">Annual GHI (kWh/m²)</TableCell>
                    <TableCell align="right">Mean wind (m/s)</TableCell>
                    <TableCell align="center">Normalized</TableCell>
                    <TableCell align="center">Raw</TableCell>
                    <TableCell align="center">Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {imports.map((row) => {
                    const warnings = row.validation?.warnings || [];
                    const notes = row.validation?.info || [];
                    const hasDetail = warnings.length > 0 || notes.length > 0;
                    const isExpanded = Boolean(expandedWarnings[row.id]);
                    // One toggle for both channels: the user wants to know
                    // "what should I know about this dataset", not to hunt
                    // through two separate affordances.
                    const chipColor = warnings.length > 0 ? "warning" : "info";
                    const chipLabel = weatherReportCount(warnings.length, notes.length, isGreek);
                    return (
                    <Fragment key={row.id}>
                    <TableRow
                      hover
                      // Drop the row's bottom rule when a detail panel follows,
                      // so the panel reads as part of this row, not a new one.
                      sx={hasDetail ? { "& > td": { borderBottom: 0 } } : undefined}
                    >
                      {/* Wide enough for a synthetic fetch name in full, e.g.
                          cams_fetch_2022_38.000_22.000.csv. Still capped with noWrap
                          so a long user filename cannot push the table sideways. */}
                      <TableCell sx={{ maxWidth: 340, minWidth: 300 }}>
                        <Tooltip title={row.original_filename || ""}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {row.original_filename || row.id}
                          </Typography>
                        </Tooltip>
                        {hasDetail && (
                          <Chip
                            size="small"
                            color={chipColor}
                            variant="outlined"
                            clickable
                            onClick={() => toggleWarnings(row.id)}
                            aria-expanded={isExpanded}
                            aria-label={`${chipLabel} — ${isGreek ? (isExpanded ? "απόκρυψη λεπτομερειών" : "εμφάνιση λεπτομερειών") : (isExpanded ? "hide details" : "show details")}`}
                            icon={
                              <ExpandMoreIcon
                                sx={{
                                  fontSize: 15,
                                  transition: "transform 150ms",
                                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                }}
                              />
                            }
                            label={chipLabel}
                            sx={{
                              mt: 0.3,
                              height: 20,
                              fontSize: 11,
                              cursor: "pointer",
                              "& .MuiChip-icon": { ml: 0.4, mr: -0.3 },
                            }}
                          />
                        )}
                        {/* Read-only: assignment happens in WeatherSourceSelector.
                            Shown here so it is obvious which dataset a study
                            depends on before deleting or replacing it. */}
                        {((hasPv && row.is_active_pv) || (hasWind && row.is_active_wind)) && (
                          <Chip
                            size="small"
                            color="primary"
                            label={`In use: ${[
                              hasPv && row.is_active_pv ? "PV" : null,
                              hasWind && row.is_active_wind ? "Wind" : null,
                            ]
                              .filter(Boolean)
                              .join(" + ")}`}
                            sx={{ mt: 0.3, ml: 0.5, height: 20, fontSize: 11, fontWeight: 700 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Rows written before the origin column existed default to
                            "upload" server-side, matching how the selector files them. */}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={row.origin === "fetch" ? "Provider fetch" : "Uploaded"}
                        />
                      </TableCell>
                      <TableCell>{FORMAT_LABELS[row.source_format] || row.source_format}</TableCell>
                      <TableCell align="right">{row.annual_ghi_kwh_m2 ?? "—"}</TableCell>
                      <TableCell align="right">{row.mean_wind_speed_ms ?? "—"}</TableCell>
                      {/* Two explicit download buttons instead of one menu. The
                          menu hid which artifact you were getting and gave no
                          feedback; and "raw" is disabled outright when the source
                          file was never retained, rather than offering a download
                          that cannot be served. */}
                      <TableCell align="center">
                        <Tooltip title="Normalized dataset — the hourly rows the simulation reads">
                          <span>
                            <IconButton
                              size="small"
                              disabled={Boolean(downloading)}
                              onClick={() => handleDownload(row, "normalized")}
                            >
                              {downloading === `${row.id}:normalized` ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            row.has_raw
                              ? row.origin === "fetch" && row.validation?.raw_provenance !== "provider_response"
                                ? (isGreek ? "Αποθηκευμένη εξαγωγή CSV πριν από την κανονικοποίηση· δεν είναι η αυτούσια απόκριση του παρόχου. Για αυθεντικό Raw PVGIS κάντε νέα λήψη." : "Saved CSV export before normalization; not the original provider response. Fetch PVGIS again for an original Raw file.")
                                : (isGreek ? "Αρχικό αρχείο — αυτούσιο όπως παραλήφθηκε, πριν από την επεξεργασία" : "Original file — exactly as received, before processing")
                              : (isGreek ? "Δεν έχει διατηρηθεί αρχικό αρχείο για αυτά τα δεδομένα" : "Raw source not retained for this dataset")
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!row.has_raw || Boolean(downloading)}
                              onClick={() => handleDownload(row, "raw")}
                            >
                              {downloading === `${row.id}:raw` ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete this dataset">
                          <span>
                            <IconButton
                              size="small"
                              disabled={Boolean(downloading)}
                              onClick={() => handleDelete(row)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {hasDetail && (
                      <TableRow>
                        {/* Must equal the header column count (File, Source, Format,
                            GHI, wind, Normalized, Raw, Delete) or the expanded notes
                            row misaligns. */}
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                my: 1,
                                px: 1.5,
                                py: 1,
                                borderLeft: "3px solid",
                                borderColor: warnings.length > 0 ? "warning.main" : "info.main",
                                bgcolor: "action.hover",
                                borderRadius: 1,
                              }}
                            >
                              <Stack spacing={1}>
                                {warnings.length > 0 && (
                                  <Stack spacing={0.75}>
                                    {warnings.map((w, i) => (
                                      <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                                        <WarningAmberOutlinedIcon
                                          sx={{ fontSize: 16, color: "warning.main", mt: "1px", flexShrink: 0 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                          {reportText(w)}
                                        </Typography>
                                      </Stack>
                                    ))}
                                  </Stack>
                                )}
                                {notes.length > 0 && (
                                  <Stack spacing={0.75}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700, color: "text.primary" }}
                                    >
                                      How this dataset will be used
                                    </Typography>
                                    {notes.map((m, i) => (
                                      <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: 16, color: "info.main", mt: "1px", flexShrink: 0 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                          {reportText(m)}
                                        </Typography>
                                      </Stack>
                                    ))}
                                  </Stack>
                                )}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* The download menu that used to live here is gone: the two
                artefacts now have their own columns in the table, so which one
                you are getting is visible before you click, and "raw" greys out
                when the source was never retained. */}

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
